import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Link from 'next/link';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { useTranslations } from '@libs/translations';
import { replaceContentFromVariable, useEditorIframe, replaceCodeBlocks } from '@libs';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { TimeRanges } from '@components/Help-Center-Components/time-ranges';
import { Points, textSize, marginLevel, common } from '@styles';
import { HELP_CENTER } from '@constants';

const TABLET_WIDTH = '600px';
const LAPTOP_WIDTH = '640px';
const FULLHD_WIDTH = '690px';
const OVERAGE_SYMBOLS_PER_MIN = 900;

export const ArticlePage = ({ data, isCourse = false, isShowCategory = true }) => {
  const { locale } = useSelector((state) => state.settings);
  const translations = useTranslations();
  const [isMobile, setIsMobile] = useState(true);
  const [content, setContent] = useState(data.body);
  const bodyRef = useRef();

  const isOpenInEditorIframe = useEditorIframe();

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 1024);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  let links;

  useEffect(() => {
    const updateData = replaceCodeBlocks(replaceContentFromVariable(data.body, translations.t));

    setContent(updateData);
  }, [data.body]);

  if (isCourse) {
    links = [
      {
        href: `/${HELP_CENTER.MAIN}`,
        name: translations.t('site.help.title'),
      },
      {
        href: `/${HELP_CENTER.MAIN}/${data.course.sectionSlug}`,
        name: `${data.course.sectionCaption}`,
      },
      {
        href: `/${HELP_CENTER.MAIN}/${data.course.sectionSlug}/${data.course.courseSlug}`,
        name: `${data.course.courseCaption}`,
      },
      {
        href: ``,
        name: `${data.caption}`,
      },
    ];
  } else if (isShowCategory) {
    links = [
      {
        href: `/${HELP_CENTER.MAIN}`,
        name: translations.t('site.help.title'),
      },
      {
        href: `/${HELP_CENTER.MAIN}/${data.section}`,
        name: `${data.section_name}`,
      },
      {
        href: `/${HELP_CENTER.MAIN}/${data.section}/${data.category}`,
        name: `${data.category_name}`,
      },
      {
        href: ``,
        name: `${data.caption}`,
      },
    ];
  } else {
    links = [
      {
        href: `/${HELP_CENTER.MAIN}`,
        name: translations.t('site.help.title'),
      },
      {
        href: `/${HELP_CENTER.MAIN}/${data.section}`,
        name: `${data.section_name}`,
      },
      {
        href: ``,
        name: `${data.caption}`,
      },
    ];
  }

  const linkSigns = {
    en: {
      prev: 'Previous lesson',
      next: 'Next lesson',
    },
    ru: {
      prev: 'Предыдущий урок',
      next: 'Следующий урок',
    },
    de: {
      prev: 'Vorherige Lektion',
      next: 'Nächste Lektion',
    },
    es: {
      prev: 'Lección anterior',
      next: 'Siguiente lección',
    },
  };

  const addEventListenerCopyButtons = () => {
    if (!bodyRef.current) return;

    const buttons = bodyRef.current.querySelectorAll('.code-box .copy-btn');

    buttons.forEach((button) => {
      const label = button.textContent;
      let timer = null;

      button.addEventListener('click', () => {
        const code = button.closest('.code-box').querySelector('code');

        if (!code || !navigator.clipboard) return;

        navigator.clipboard
          .writeText(code.textContent)
          .then(() => {
            button.textContent = button.dataset.copiedLabel || label;
            clearTimeout(timer);
            timer = setTimeout(() => {
              button.textContent = label;
            }, 2000);
          })
          .catch(() => {});
      });
    });
  };

  useEffect(() => {
    setTimeout(() => {
      addEventListenerCopyButtons();
    }, 0);
  }, [content.id]);

  const currentPageIndex = isCourse
    ? data.asideNav?.findIndex((item) => item.slug === `${data.section}/${data.category}/${data.slug}`)
    : data.asideNav?.findIndex((item) => item.slug === data.slug);

  const readingTime = Math.ceil(content.length / OVERAGE_SYMBOLS_PER_MIN);
  return (
    <Container>
      <Article>
        <Breadcrumbs links={links} isOpenInEditorIframe={isOpenInEditorIframe} />
        <Title>{data.caption}</Title>
        {!isCourse && data.courses.length > 0 && (
          <Courses isOpenInEditorIframe={isOpenInEditorIframe}>
            <span>{translations.t('site.help.articleInfo')} </span>
            {data.courses.map((tag) => (
              <Link key={tag.slug} href={`/${HELP_CENTER.MAIN}/${tag.slug}`} passHref>
                <Tag>{tag.caption}</Tag>
              </Link>
            ))}
          </Courses>
        )}
        <InfoWrap>
          <TimeRanges time={data.time ?? readingTime} locale={locale} />
          <Views>{data.page_views}</Views>
        </InfoWrap>
        <Content ref={bodyRef} dangerouslySetInnerHTML={{ __html: content }} />
      </Article>
      {data.asideNav.length > 1 && (
        <AsideContent>
          <AsideTitle>
            {isCourse ? translations.t('site.help.moduleNav') : translations.t('site.help.articleNav')}
          </AsideTitle>
          <Navigation>
            {data.asideNav?.map((item, index) => {
              return index === currentPageIndex ? (
                <NavLink key={item.slug} aria-label={item.caption} isCurrent={true}>
                  {item.caption}
                </NavLink>
              ) : (
                <Link
                  key={item.slug}
                  href={
                    isCourse
                      ? `/${HELP_CENTER.MAIN}/${item.slug}`
                      : `/${HELP_CENTER.MAIN}/${data.section}/${data.category}/${item.slug}`
                  }
                  passHref>
                  <NavLink aria-label={item.caption}>{item.caption}</NavLink>
                </Link>
              );
            })}
          </Navigation>
        </AsideContent>
      )}
      {isCourse && (
        <CourseNavigation>
          {currentPageIndex !== 0 && (
            <Link href={`/${HELP_CENTER.MAIN}/${data.asideNav[currentPageIndex - 1].slug}`} passHref>
              <PrevLink aria-label={`${data.asideNav[currentPageIndex - 1].slug}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20 12L3 12M3 12L8.67157 18M3 12L8.67157 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {!isMobile && linkSigns[locale].prev}
              </PrevLink>
            </Link>
          )}
          {currentPageIndex !== data.asideNav.length - 1 && (
            <Link href={`/${HELP_CENTER.MAIN}/${data.asideNav[currentPageIndex + 1].slug}`} passHref>
              <NextLink aria-label={`${linkSigns[locale].prev}`}>
                {!isMobile && linkSigns[locale].next}
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12L21 12M21 12L15.3284 6M21 12L15.3284 18" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </NextLink>
            </Link>
          )}
        </CourseNavigation>
      )}
    </Container>
  );
};

const Container = styled.div`
  margin: 55px 16px 80px;

  @media ${Points.tablet_landscape} {
    display: grid;
    grid-template-columns: 1fr 200px;
    margin: 30px 28px 100px;
  }

  @media ${Points.laptop_wide} {
    grid-template-columns: 1fr 240px;
    width: 1280px;
    margin: 40px auto 120px;
  }

  @media ${Points.fullhd} {
    grid-template-columns: 1fr 266px;
    width: 1380px;
    margin: 40px auto 160px;
  }
`;

const Article = styled.article`
  ${textSize('article')};
  @media ${Points.tablet_landscape} {
    width: ${TABLET_WIDTH};
  }

  @media ${Points.laptop_wide} {
    width: ${LAPTOP_WIDTH};
  }

  @media ${Points.fullhd} {
    width: ${FULLHD_WIDTH};
  }
`;

const Title = styled.h1`
  margin: 35px 0 15px;
  ${textSize('block_header')};
  font-weight: 200;
  color: ${Colors.main_black};

  @media ${Points.tablet_landscape} {
    margin: 35px 0 20px;
  }

  @media ${Points.laptop_wide} {
    margin: 40px 0 25px;
  }

  @media ${Points.fullhd} {
    margin: 50px 0 20px;
  }
`;

const Courses = styled.nav`
  display: flex;
  flex-wrap: ${(p) => {
    p.isOpenInEditorIframe ? 'wrap' : 'nowrap';
  }};
  gap: 10px;
  overflow-x: auto;
  white-space: ${(p) => {
    !p.isOpenInEditorIframe && 'nowrap';
  }};
  margin-bottom: 20px;
  -ms-overflow-style: none;
  scrollbar-width: none;
  color: ${Colors.main_gray};
  ${textSize('description')};

  &::-webkit-scrollbar {
    display: none;
  }

  & span {
    align-self: center;
  }
`;

const InfoWrap = styled.div`
  display: flex;
  gap: 20px;
`;

const Tag = styled.a`
  ${Mixins.resetLink()};
  background-color: ${Colors.blue_200};
  padding: 5px 10px 6px;
  border-radius: 5px;
  ${textSize('description_2')};
  color: ${Colors.blue_900};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      box-shadow: 0px 8px 24px -4px #29308614, 0px 6px 12px -6px #2930861f;
    }
  }

  &:active {
    background-color: ${Colors.main_blue};
    color: ${Colors.white};
  }
`;

const Views = styled.div`
  padding-left: 21px;
  background-image: url('/images/help/views.png');
  background-position: left center;
  background-repeat: no-repeat;
  background-size: 16px;
  ${textSize('description')};
  color: ${Colors.second_gray};
  margin-top: 5px;

  @media ${Points.fullhd} {
    margin-top: 10px;
  }
`;

const Content = styled.div`
  margin-top: 40px;

  & > *:last-child {
    margin-bottom: 0;
  }

  @media ${Points.laptop_wide} {
    margin-top: 50px;
  }

  @media ${Points.fullhd} {
    margin-top: 60px;
  }

  h2 {
    color: ${Colors.main_dark};
    ${textSize('large_card_header')};
    font-weight: 400;
    ${marginLevel('bottom', 'accent_outer')};
    ${marginLevel('top', 'blog_header')};
  }

  .interview-question {
    ${textSize('subtitle')};
    font-weight: 400;
    color: ${Colors.main_dark};
    ${marginLevel('bottom', 'interview')};
    ${marginLevel('top', 'accent_outer')};
  }

  .video {
    position: relative;
    display: flex;
    padding-top: 56.25%;

    iframe {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
  }

  figure {
    ${marginLevel('top', 'blog_img')};
    ${marginLevel('bottom', 'accent_outer')};
    margin-left: auto;
    margin-right: auto;
  }

  figure:not(.video):not(.column):not(.mount) {
    @media ${Points.laptop} {
      width: ${LAPTOP_WIDTH};
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_WIDTH};
    }
  }

  figcaption {
    margin-top: 5px;
    font-size: 12px;
    line-height: 1.115;
    color: ${Colors.main_gray};
    text-align: right;

    @media ${Points.fullhd} {
      font-size: 14px;
    }
  }

  img {
    width: 100%;
    display: block;
  }

  .column,
  .column img {
    width: 100%;
  }

  .border img {
    border: 1px solid ${Colors.light};
  }

  .image-pair-wrapper {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  .mount {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 24px;
    background-color: #656565;

    @media ${Points.tablet_portrait} {
      padding-top: 25px;
    }

    @media ${Points.laptop} {
      padding-top: 30px;
    }
  }

  .mount img {
    width: 90%;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 28px;

    @media ${Points.tablet_portrait} {
      width: fit-content;
      max-width: 90%;
      margin-bottom: 30px;
    }

    @media ${Points.laptop} {
      margin-bottom: 35px;
    }
  }

  .mount figcaption {
    background-color: ${Colors.white};
    border: 1px solid ${Colors.white};
    width: 100%;
    padding-top: 5px;
  }

  .visual-heading {
    margin-bottom: 10px;
    font-weight: 300;
    font-size: 20px;
    line-height: 1.2;

    @media ${Points.tablet_portrait} {
      font-size: 22px;
      margin-left: auto;
      margin-right: auto;
    }

    @media ${Points.laptop_wide} {
      font-size: 24px;
      line-height: 1.1;
    }

    @media ${Points.fullhd} {
      font-size: 30px;
      line-height: 1.115;
    }
  }

  p:not(.bluebox p):not(.ctabox p):not(.list__content p):not(.visual-heading):not(.quote-text p) {
    color: ${Colors.main_gray};
    margin-bottom: 20px;
    ${textSize('article')};

    @media ${Points.tablet_portrait} {
      margin-left: auto;
      margin-right: auto;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  p:not(.bluebox p):not(.ctabox p):not(.list__content p):not(.visual-heading):not(.quote-text p):has(+ ul),
  p:not(.bluebox p):not(.ctabox p):not(.list__content p):not(.visual-heading):not(.quote-text p):has(+ ol) {
    margin-bottom: 10px;
  }

  ul {
    ${common('listBlog')};
  }

  ol,
  ul {
    color: ${Colors.main_gray};
    ${marginLevel('bottom', 'accent_outer')};
    padding-left: 20px;

    @media ${Points.tablet_portrait} {
      margin-left: auto;
      margin-right: auto;
    }
  }

  .list__content .visual-heading {
    display: block;
    font-size: 18px;
    line-height: 1.2;
    font-weight: 400;
    color: ${Colors.main_dark};
    margin-bottom: 10px;
    margin-top: 0;

    @media ${Points.tablet_portrait} {
      font-size: 20px;
    }

    @media ${Points.laptop} {
      padding-top: 5px;
    }

    @media ${Points.fullhd} {
      font-size: 24px;
      padding-top: 6px;
    }
  }

  .list__content ul {
    margin-bottom: 0;
  }

  li {
    ${textSize('article')};
    margin-bottom: 10px;

    @media ${Points.laptop} {
      margin-bottom: 14px;
    }
  }

  a:not(.bluebox-button):not(.cta-button):not(.cta-button-text) {
    ${common('a')};
    color: ${Colors.second_link};
  }

  .list {
    display: flex;
    justify-content: start;
    ${marginLevel('both', 'whisper')};

    p {
      padding: 0;
    }
  }

  .list + :not(.list) {
    ${marginLevel('top', 'accent_outer')};
  }

  b,
  strong:not(.bluebox strong):not(.ctabox strong) {
    font-weight: 500;
  }

  br {
    height: 24px;

    @media ${Points.tablet_portrait} {
      height: 25px;
    }

    @media ${Points.laptop} {
      height: 20px;
    }

    @media ${Points.fullhd} {
      height: 30px;
    }
  }

  br + br {
    height: 24px;

    @media ${Points.tablet_portrait} {
      height: 20px;
    }

    @media ${Points.laptop} {
      height: 20px;
    }

    @media ${Points.fullhd} {
      height: 20px;
    }
  }

  .bluebox,
  .ctabox,
  .quote {
    width: 100%;
  }

  .bluebox .visual-heading,
  .ctabox .visual-heading {
    margin-top: 0;
    padding-top: 0;
  }

  .bluebox p > strong,
  .bluebox p > b {
    font-weight: 500;
  }

  code {
    font-family: 'CoFoSans Mono', monospace;
    font-weight: 400;
    letter-spacing: 0%;
    font-variant-numeric-fraction: stacked-fractions;
    background-color: ${Colors.beige_100};
    padding: 6px;
    border-radius: 4px;
  }

  .bluebox p > code {
    background-color: transparent;
  }

  .code-box {
    position: relative;
    margin-bottom: 30px;
    background-color: ${Colors.main_dark};
    color: #f8f8f2;
    padding: 15px 15px 5px;
    border-radius: 8px;
    font-family: 'Courier New', Courier, monospace;

    @media ${Points.tablet_portrait} {
      width: 600px;
      margin: 20px auto 30px;
    }

    @media ${Points.laptop} {
      width: 640px;
    }

    @media ${Points.fullhd} {
      width: 690px;
    }
  }

  .copy-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: ${Colors.blue_500};
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    opacity: 0;
    transition: opacity 0.2s, background-color 0.2s;
  }

  .code-box:hover .copy-btn,
  .copy-btn:focus-visible {
    opacity: 1;
  }

  @media (hover: none) {
    .copy-btn {
      opacity: 1;
    }
  }

  .copy-btn:hover {
    background-color: ${Colors.blue_400};
  }

  pre {
    margin: 0;
    overflow-x: auto;
  }

  .code-box pre {
    padding-bottom: 10px;

    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
  }

  .code-box pre::-webkit-scrollbar {
    height: 5px;
    background: transparent;
  }

  .code-box pre::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }

  pre code {
    color: #f8f8f2;
    background-color: transparent;
    padding: 0;
  }

  .callout {
    margin: 20px 0;
    padding: 15px;
    padding-left: 46px;
    background-color: #f2e9e2;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='%233F3F3F'%3e%3cpath d='M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464a256 256 0 1 0 0-512 256 256 0 1 0 0 512zM216 336c-13.3 0-24 10.7-24 24s10.7 24 24 24l80 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-8 0 0-88c0-13.3-10.7-24-24-24l-48 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l24 0 0 64-24 0zm40-144a32 32 0 1 0 0-64 32 32 0 1 0 0 64z'/%3e%3c/svg%3e");
    background-size: 16px 16px;
    background-position: 15px 17px;
    background-repeat: no-repeat;

    @media ${Points.tablet_portrait} {
      width: 600px;
      margin: 20px auto;
    }

    @media ${Points.laptop} {
      width: 640px;
    }

    @media ${Points.fullhd} {
      width: 690px;
    }
  }

  .callout.warning {
    background-color: #f2e9e2;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='%232c35ef'%3e%3cpath d='M256 512a256 256 0 1 1 0-512 256 256 0 1 1 0 512zm0-464a208 208 0 1 0 0 416 208 208 0 1 0 0-416zm0 336a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm0-256c18.2 0 32.7 15.5 31.4 33.7l-7.4 104c-.9 12.6-11.4 22.3-23.9 22.3-12.6 0-23-9.7-23.9-22.3l-7.4-104C223.3 143.5 237.8 128 256 128z'/%3e%3c/svg%3e");
  }

  .callout.danger {
    background-color: #f2e9e2;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='%23e76422'%3e%3cpath d='M256.5 0c14.7 0 28.2 8.1 35.2 21l216 400c6.7 12.4 6.4 27.4-.8 39.5S486.6 480 472.5 480l-432 0c-14.1 0-27.1-7.4-34.3-19.5s-7.5-27.1-.8-39.5l216-400c7-12.9 20.5-21 35.2-21zM53.9 432L459.1 432 256.5 56.8 53.9 432zm202.6-40a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm0-208c18.6 0 33 16.1 31 34.6l-7.1 64.1C279 294.8 268.7 304 256.5 304s-22.5-9.2-23.8-21.3l-7.1-64.1c-2-18.5 12.4-34.6 31-34.6z'/%3e%3c/svg%3e");
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 20px 0;

    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    @media ${Points.tablet_portrait} {
      width: 750px;
      display: table;
      overflow-x: visible;
    }

    @media ${Points.laptop} {
      width: 900px;
    }
  }

  th,
  td {
    max-width: calc(100vw - 16px);

    @media ${Points.tablet_portrait} {
      max-width: none;
    }
  }

  th {
    padding: 10px 8px;
    border: 1px solid #ddd;
    font-weight: 300;
    text-align: left;
  }

  thead th {
    text-align: center;
    font-weight: 500;
    background-color: #eaeaea;
  }
`;

const AsideContent = styled.aside`
  display: none;

  @media ${Points.tablet_landscape} {
    display: block;
    margin-top: 100px;
  }

  @media ${Points.laptop_wide} {
    margin-top: 120px;
  }

  @media ${Points.fullhd} {
    margin-top: 160px;
  }
`;

const AsideTitle = styled.h3`
  ${textSize('body')};
  font-weight: 400;

  @media ${Points.tablet_landscape} {
    margin-bottom: 20px;
  }

  @media ${Points.fullhd} {
    margin-bottom: 25px;
  }
`;

const Navigation = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const NavLink = styled.a`
  ${Mixins.resetLink()};
  ${textSize('description')};
  color: ${(p) => (p.isCurrent ? Colors.main_blue : Colors.second_gray)};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${(p) => !p.isCurrent && Colors.second_blue};
    }
  }

  &:active {
    color: ${(p) => !p.isCurrent && Colors.main_blue};
  }
`;

const CourseNavigation = styled.nav`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: auto 1fr auto;
  margin-top: 80px;

  @media ${Points.tablet_landscape} {
    margin-top: 100px;
  }

  @media ${Points.laptop_wide} {
    margin-top: 120px;
  }

  @media ${Points.fullhd} {
    margin-top: 160px;
  }
`;

const PrevLink = styled.a`
  ${Mixins.resetLink()};
  display: flex;
  align-self: center;
  gap: 5px;
  grid-column: 1;
  grid-row: 1;
  justify-items: left;

  & svg {
    stroke: ${Colors.graphite};
    position: relative;
    top: -2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${Colors.second_blue};

      & svg {
        stroke: ${Colors.second_blue};
      }
    }
  }

  &:active {
    color: ${Colors.main_blue};

    & svg {
      stroke: ${Colors.main_blue};
    }
  }
`;

const NextLink = styled.a`
  ${Mixins.resetLink()};
  display: flex;
  align-self: center;
  gap: 5px;
  grid-column: 3;
  grid-row: 1;
  justify-items: right;

  & svg {
    stroke: ${Colors.graphite};
    position: relative;
    top: -2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${Colors.second_blue};

      & svg {
        stroke: ${Colors.second_blue};
      }
    }
  }

  &:active {
    color: ${Colors.main_blue};

    & svg {
      stroke: ${Colors.main_blue};
    }
  }

  .list__content p {
    font-weight: 400;
  }
`;
